import { nanoid } from "@reduxjs/toolkit";

import {
    calcRoleTreeItemStatus,
    calcRoleTreeItemTrafficLightStatus,
    REPORT_MODELING_DELTA_STATUSES,
    setClassCheckedIndeterminate,
    sortTreeChildren,
} from "../utils/reports-modeling-utils";
import { createTreeNode } from "../utils/tree-table.js";
import { addEncodedURIComponent } from "../utils/addEncodedURIComponent.js";

import ReportsService from "./reports-service.js";
import SodSocketService from "./sod-socket-service.js";
import {
    RoleVariantDetailed,
    ModelingRole,
    RoleChildren,
    RoleForView,
    SaveVariantDataType,
    RoleVariant,
    GetRoleVariantDetailedResponse,
    GetRoleForViewResponse,
} from "../types/role-modeling/role-modeling-types";
import { GroupRoleChildrenResponse, RoleMenuNode, TreeNodeResponse, FieldResponse } from "../types/common/modeling/modeling-types";
import { GetModelingRoleMenuItem, GetModelingRoleTreeField, GetModelingRoleTreeItem, GetPermissionTreeItemResponse, VariantRaw } from "types/common/modeling/service-types";
import {
    EmployeeDetailedVariant,
    EmployeeVariant,
    GetEmployeeRolesForView,
    GetEmployeeDetailedVariantResponse,
    GetModelingEmployeeResponse,
    ModelingUser,
} from "../types/user-modeling/user-modeling-types";
import intl from "../i18n/i18n";

class ReportsModelingService extends ReportsService {
    async getSessionId() {
        const socketService = SodSocketService.getInstance() as unknown as { getSessionId(): Promise<string> };

        return await socketService?.getSessionId();
    }

    async getVariantsByUser(params: { variant_type: string }, redirectWhenNotFound = false): Promise<EmployeeVariant[] | RoleVariant[]> {
        const config = this.generateConfigForGetEntities(params);
        const data = await this.get("/sod/reports/variants", config, true, redirectWhenNotFound);

        const variants = data?.map((variant) => this.parseCommonVariant(variant));

        return variants;
    }

    async getDetailedRoleVariant(variant: RoleVariantDetailed): Promise<RoleVariantDetailed> {
        const detailedVariantRaw = await this.get(`/sod/reports/role-variant/${variant?.id}`);

        return this.parseRoleVariant(detailedVariantRaw);
    }
    async getDetailedEmployeeVariant(variant: EmployeeVariant): Promise<EmployeeDetailedVariant> {
        const detailedVariantRaw = await this.get(`/sod/reports/employee-variant/${variant?.id}`);

        return this.parseEmployeeVariant(detailedVariantRaw);
    }

    async getDetailedIntegrationRole(role: string, systemId: string): Promise<ModelingRole | null> {
        if (!role || !systemId) {
            return null;
        }

        const params = {
            system_id: systemId,
            role: role,
        };

        const config = this.generateConfigForGetEntities(params);

        const data = await this.get(`/core/custom-roles/detailed`, config);
        const tree = this.parseRolePermissionsTree(data?.tree);
        const orgLevels = this.parseRoleOrgLevels(data?.org_levels);
        const menu = this.parseRoleMenu(data?.menu);
        const children = this.parseRoleChildrenNew(data?.childrens || [], systemId, data?.role);

        return {
            id: nanoid(),
            role: data.role,
            systemId: systemId,
            description: data.description,
            tree: tree,
            children: children,
            menu: menu,
            orgLevels: orgLevels,
            delta: [],
            initialized: true,
            isComplex: data.is_complex_role,
            profile: data.profile || role,
            orgLevelsDelta: [],
            status: null,
            added: false,
            changed: false,
            isStandard: data.is_standart,
        };
    }

    async getDetailedVariantRole(variant_id: number, role: string, systemId: string): Promise<ModelingRole | null> {
        if (!role || !systemId) {
            return null;
        }

        const params = {
            system_id: systemId,
            role: role,
        };

        const config = this.generateConfigForGetEntities(params);

        const data = await this.get(`/sod/reports/detailed-variant-role?role_name=${role}&variant_id=${variant_id}`, config);
        const tree = this.parseRolePermissionsTree(data.tree);
        const orgLevels = this.parseRoleOrgLevels(data.org_levels);
        const menu = this.parseRoleMenu(data.menu);
        const children = this.parseRoleChildrenNew(data.childrens || [], systemId, data.role_name);

        return {
            id: nanoid(),
            role: data.role_name,
            systemId: systemId,
            description: data.description,
            tree: tree,
            children: children,
            menu: menu,
            orgLevels: orgLevels,
            delta: [],
            initialized: true,
            isComplex: data.is_complex_role,
            profile: data.profile || role,
            orgLevelsDelta: [],
            status: data.status || null,
            changed: false,
            added: false,
        };
    }

    async saveEmployeeModelingVariant(form, isNew = true): Promise<EmployeeDetailedVariant> {
        const save = isNew ? this.post : this.put;

        const data = await save.call(this, "/sod/reports/employee-variant", form);

        return this.parseEmployeeVariant(data);
    }

    async saveRoleVariant(form: SaveVariantDataType, isNew = true): Promise<RoleVariantDetailed> {
        const save = isNew ? this.post : this.put;

        const data = await save.call(this, "/sod/reports/role-variant", form);

        return this.parseRoleVariant(data);
    }

    getOperation(roleForView: RoleForView | null, childRole: RoleChildren | null, modelingRole: ModelingRole): string | null {
        if (roleForView && roleForView.status) {
            return roleForView.status;
        }

        if (childRole && childRole.status) {
            return childRole.status;
        }

        if (modelingRole.changed) {
            return REPORT_MODELING_DELTA_STATUSES.CHANGED;
        }

        return null;
    }

    parseModelingGroupRolesForServer(rolesForView: RoleForView[]) {
        return rolesForView.map((role) => ({
            role: role.role,
            system_id: role.systemId,
            children: role.children.map((child) => child.role),
        }));
    }

    parseModelingRolesVariantForServer(modelingRoles: ModelingRole[] = [], rolesForView: RoleForView[]) {
        const rolesForViewMap = {};
        const rolesChildrenMap = {};

        rolesForView.forEach((role) => {
            rolesForViewMap[role.role] = role;

            role.children.forEach((childRole) => {
                rolesChildrenMap[childRole.role] = childRole;
            });
        });

        return modelingRoles
            .filter((role) => role)
            .map((role) => {
                const roleForView: RoleForView | null = rolesForViewMap[role?.role];
                const roleChild: RoleChildren | null = rolesChildrenMap[role?.role];

                const parsedChildren = reportsModelingService.parseRoleChildrenVariant((roleForView && roleForView.children) || []);

                const parsedRole = reportsModelingService.parseRoleToTreeVariant(role);

                return {
                    ...parsedRole,
                    children: parsedChildren,
                    role: role?.role,
                    system_id: role?.systemId,
                    is_complex: role?.isComplex,
                    description: role?.description,
                    changed: !!role?.changed,
                    added: !!role?.added,
                    is_explicit: role ? !!rolesForViewMap[role.role] : false,
                    operation: this.getOperation(roleForView, roleChild, role),
                };
            });
    }

    parseModelingEmployeeForServer(employee: ModelingUser) {
        return {
            employee: employee.employee,
            system_id: employee.systemId,
            roles: employee.roles.flatMap((role) => (role.isStandard ? [] : [{ status: role.status || "", role: role.role }])),
        };
    }
    parseCommonVariant(variantRaw: VariantRaw): RoleVariant | EmployeeVariant {
        const baseVariant = {
            id: variantRaw.id,
            name: variantRaw.variant_name,
            owner: variantRaw.variant_owner,
            type: variantRaw.variant_type,
            inFavorites: variantRaw.in_favorites,
            data: {
                systems: variantRaw.variant_data.systems,
                risks: variantRaw.variant_data.risks,
                matrixHeaders: variantRaw.variant_data.matrix_headers,
                riskLevels: variantRaw.variant_data.risk_levels,
            },
        };

        if (variantRaw.variant_data?.roles) {
            return {
                ...baseVariant,
                data: {
                    ...baseVariant.data,
                    roles: variantRaw.variant_data.roles,
                },
            };
        } else if (variantRaw.variant_data?.users) {
            return {
                ...baseVariant,
                data: {
                    ...baseVariant.data,
                    users: variantRaw.variant_data.users,
                },
            };
        }
        throw Error("Invalid variant data");
    }
    parseRoleVariant(variantRaw: GetRoleVariantDetailedResponse): RoleVariantDetailed {
        return {
            id: variantRaw.id,
            name: variantRaw.variant_name,
            owner: variantRaw.variant_owner,
            type: variantRaw.variant_type,
            inFavorites: variantRaw.in_favorites,
            roles: variantRaw.roles.map((role) => this.parseVariantRoleForView(role)),
            data: {
                systems: variantRaw.variant_data.systems,
                risks: variantRaw.variant_data.risks,
                roles: variantRaw.variant_data.roles,
                matrixHeaders: variantRaw.variant_data.matrix_headers,
                riskLevels: variantRaw.variant_data.risk_levels,
            },
        };
    }

    parseEmployeeVariant(variantRaw: GetEmployeeDetailedVariantResponse): EmployeeDetailedVariant {
        return {
            id: variantRaw.id,
            name: variantRaw.variant_name,
            owner: variantRaw.variant_owner,
            inFavorites: variantRaw.in_favorites,
            type: variantRaw.variant_type,
            employees: variantRaw.employees.map((employee) => this.parseModelingUser(employee, variantRaw.roles)),
            data: {
                systems: variantRaw.variant_data.systems,
                risks: variantRaw.variant_data.risks,
                users: variantRaw.variant_data.users,
                matrixHeaders: variantRaw.variant_data.matrix_headers,
                riskLevels: variantRaw.variant_data.risk_levels,
                groups: null,
            },
        };
    }

    parseVariantRoleForView(role: GetRoleForViewResponse): RoleForView {
        return {
            id: nanoid(),
            role: role.role,
            systemId: role.system_id,
            description: role.description,
            isComplex: role.is_complex,
            status: role.operation,
            children: (role.children ?? []).map((child) => ({
                id: nanoid(),
                role: child.role,
                description: child.description,
                systemId: child.system_id,
                changed: child.is_changed,
                parentRole: role.role,
                status: child.operation,
                isComplex: false,
            })),
            open: false,
        };
    }

    parseRoleChildrenNew(children: GroupRoleChildrenResponse[], systemId: string, parentRole: string): RoleChildren[] {
        if (!children.length) {
            return [];
        }

        return children.map((childrenRole) => ({
            id: nanoid(),
            role: childrenRole.role,
            systemId: systemId,
            description: childrenRole.description,
            parentRole: parentRole,
            status: childrenRole.operation,
            isComplex: false,
        }));
    }

    async getModelingRoles(roles, systems, users, variant, reportLevel) {
        const params: { roles?: string; system_ids?: string; employees?: string; levels?: string } = {};

        if (roles) {
            params.roles = roles.toString();
        }

        if (systems) {
            params.system_ids = systems.toString();
        }

        if (users) {
            params.employees = users.toString();
        }

        if (reportLevel) {
            params.levels = reportLevel;
        }

        const config = this.generateConfigForGetEntities(params);
        const url = addEncodedURIComponent("/sod/reports/simulation/roles", "variant", variant);
        const apiRoles = await this.get(url, config);

        return apiRoles?.map((role) => this.parseModelingRole(role)) ?? [];
    }

    async getModelingEmployee(employee: string, systemId: string) {
        if (!employee || !systemId) {
            return null;
        }

        const params = {
            employee: employee,
            system_id: systemId,
        };

        const config = this.generateConfigForGetEntities(params);

        const data = await this.get(`/sod/reports/simulation/employee`, config);
        // const tree = this.parseRolePermissionsTree(data.tree);
        // const orgLevels = this.parseRoleOrgLevels(data.org_levels);
        // const menu = this.parseRoleMenu(data.menu);
        // const children = this.parseRoleChildrenNew(data.childrens || [], systemId, data.role);
        // return {
        //     id: nanoid(),
        //     role: data.role,
        //     systemId: systemId,
        //     description: data.description,
        //     tree: tree,
        //     children: children,
        //     menu: menu,
        //     orgLevels: orgLevels,
        //     delta: [],
        //     initialized: true,
        //     isComplex: data.is_complex_role,
        //     profile: data.profile || role,
        //     orgLevelsDelta: [],
        //     status: null,
        //     added: false,
        //     changed: false,
        // };
        return data.employees?.map((employee) => this.parseModelingUser(employee, data.roles)) ?? [];
    }

    async getTcodePermissionsTree(params, signal, system, permissions, checkedPermissions) {
        const paramsInner = params ? { ...params } : {};

        let url = "/api/core/pfcg/tcode-permission-tree?system=";

        if (system) {
            url += `${system}`;
        }

        if (permissions) {
            url += `&permissions=${permissions.toString()}`;
        }

        const config = this.generateConfigForGetEntities(paramsInner, signal);

        if (config.params) {
            if (config.params.page) {
                url += `&page=${config.params.page}`;
            }
            if (config.params.limit) {
                url += `&limit=${config.params.limit}`;
            }
            if (config.params.search) {
                url += `&search=${config.params.search}`;
            }
        }

        const batchStruct = [{ url: url, method: "get" }];
        const data = await this.post("/core/batch/", batchStruct);

        const rows = this.parsePermissionsTree(data[0].body, checkedPermissions);

        return rows;
    }

    async getClassPermissionsTree(params, signal, system, checkedPermissions) {
        const paramsInner = params ? { ...params } : {};

        let url = "/api/core/pfcg/class-permission-tree?system=";

        if (system) {
            url += `${system}`;
        }

        const config = this.generateConfigForGetEntities(paramsInner, signal);

        if (config.params) {
            if (config.params.page) {
                url += `&page=${config.params.page}`;
            }
            if (config.params.limit) {
                url += `&limit=${config.params.limit}`;
            }
            if (config.params.search) {
                url += `&search=${config.params.search}`;
            }
        }

        const batchStruct = [{ url: url, method: "get" }];
        const data = await this.post("/core/batch/", batchStruct);

        const rows = this.parsePermissionsTree(data[0].body.rows, checkedPermissions);
        const total = data[0].body.total;

        return { rows, total };
    }

    async calcRoleLevelModelingReport(form, conflictLevel): Promise<void> {
        // const config = this.generateConfigForGetEntities(params, signal);
        const config: { headers: Record<string, string>; params: { conflict_level: string } } = {
            headers: {
                "Auth-Session": await this.getSessionId(),
            },
            params: { conflict_level: conflictLevel },
        };

        await this.post("/sod/reports/role-level/calc-simulation", form, config);
    }

    async calcRoleLevelModelingCompareReport(form) {
        const config: { headers?: Record<string, string> } = {};

        config.headers = {
            "Auth-Session": await this.getSessionId(),
        };

        const data = await this.post("/sod/reports/simulation/role-level/compare", form, config);
        return data;
    }

    async calcUserLevelModelingReport(form, conflictLevel): Promise<void> {
        const config: { headers?: Record<string, string>; params: { conflict_level: string } } = {
            headers: {
                "Auth-Session": await this.getSessionId(),
            },
            params: { conflict_level: conflictLevel },
        };

        const data = await this.post("/sod/reports/user-level/calc-simulation", form, config);
        return data;
    }

    //async initializeNewRoles(rolesPfcgForInit, roleMenuForInit, groupRolesForInit, changedOrgLevelsForInit) {
    async calcUserLevelModelingCompareReport(form) {
        const config: { headers?: Record<string, string> } = {};

        config.headers = {
            "Auth-Session": await this.getSessionId(),
        };

        const data = await this.post("/sod/reports/simulation/user-level/compare", form, config);
        return data;
    }

    // async initializeNewRoles(rolesPfcgForInit, roleMenuForInit, groupRolesForInit, changedOrgLevelsForInit) {
    //     const data = await this.post("/sod/reports/role-initialization", {
    //         changed_roles: rolesPfcgForInit,
    //         changed_group_roles: groupRolesForInit,
    //         changed_menues: roleMenuForInit,
    //         changed_org_levels: changedOrgLevelsForInit,
    //     });

    //     return data;
    // }

    async getRoleLevelModelingColumns(reportLevel: string, reportType: string, defaultColumns: string | null = null) {
        return this.getReportColumns("/sod/reports/user-role-model-column", reportLevel, reportType, defaultColumns);
    }

    async getUserLevelModelingColumns(reportLevel: string, reportType: string, defaultColumns: string | null = null) {
        return this.getReportColumns("/sod/reports/user-model-column", reportLevel, reportType, defaultColumns);
    }

    async saveRoleLevelModelingColumns(columns, reportLevel, reportType) {
        return this.saveReportColumns("/sod/reports/user-role-model-column", columns, reportLevel, reportType);
    }

    async saveUserLevelModelingColumns(columns, reportLevel, reportType) {
        return this.saveReportColumns("/sod/reports/user-model-column", columns, reportLevel, reportType);
    }

    async getRolesModelingSearchHelp(params, signal) {
        const config = this.generateConfigForGetEntities(params, signal);

        const data = await this.get("/sod/reports/search-help/simulation-roles", config);

        const rows = data && data.rows.map((item) => this.parseRole(item));
        const total = data && data.total;

        return { rows, total };
    }

    async saveGroupRolesVariant(roles, variant, reportLevel, oldVariant) {
        const delta = roles.filter((role) => role.isComplex && role.children?.length > 0).map((role) => this.parseGroupRoleForVariant(role, variant, reportLevel, oldVariant));

        if (delta.length > 0) {
            return this.post("/core/pfcg/group-role/variant", delta);
        }
    }

    async saveRoleMenuVariant(roles, variant, reportLevel) {
        const menus = roles.filter((role) => !role.isComplex && role.status).map((role) => this.parseRoletoMenuVariant(role, variant, reportLevel));

        if (menus.length > 0) {
            return this.post("/core/pfcg/role-menu/variant", menus);
        }
    }

    async _getRoleMenu(roleId, systemId, signal) {
        const params = { system: systemId };
        const config = this.generateConfigForGetEntities(params, signal);

        return this.get(`/core/integration/role-menu-tree/${roleId}`, config);
    }


    async getCopyRoleMenu(roleId, systemId, signal) {
        const data = await this._getRoleMenu(roleId, systemId, signal);
        return data && this.parseCopyRoleMenu(roleId, data);
    }

    async getRoleMenuVariant(roleId, systemId, variant, reportLevel, signal) {
        const params = {
            role: roleId,
            level: reportLevel,
            system: systemId,
        };

        const config = this.generateConfigForGetEntities(params, signal);
        const url = addEncodedURIComponent("/core/pfcg/role-menu/variant", "variant_name", variant);
        const data = await this.get(url, config);
        return data && this.parseRoleMenu(data);
    }

    async getRoleChildrensVariant(role, systemId, level, variant) {
        if (!role || !systemId) {
            return;
        }

        const params = {
            system: systemId,
            role: role,
            level: level,
            variant_name: variant,
        };

        const config = this.generateConfigForGetEntities(params);
        const data = await this.get(`/core/pfcg/group-role/variant`, config);

        const parsedChildren = this.parseRoleChildren(data, role, systemId);

        return parsedChildren;
    }

    parseRoleChildren(data, role, systemId) {
        const childrens = data[role] || [];

        return childrens.map((childrenRole) => ({
            id: nanoid(),
            role: childrenRole,
            systemId: systemId,
        }));
    }

    parseRoleChildrenList(childrens, systemId) {
        return childrens.map((childrenRole) => ({
            //id: nanoid(),
            // role: childrenRole,
            id: nanoid(),
            role: childrenRole.role,
            description: childrenRole.description,
            systemId: systemId,
        }));
    }

    parseFunctionDefaultPermission(item, systemId) {
        return {
            system: systemId,
            permission: item.permission,
            field: item.permissionf_field,
            valueFrom: item.value_from,
            valueTo: item.value_to,
        };
    }

    parseRoleMenuChildVariant(child) {
        const parsedChild = {
            report_name: child.reportName,
            report_type: child.reportType,
            is_folder: child.isFolder,
            node_text: child.nodeText,
            description: child.description,
            children: child?.children || [],
        };

        if (child.children) {
            parsedChild.children = child.children.map((subChild) => this.parseRoleMenuChildVariant(subChild));
        }

        return parsedChild;
    }

    parseRoletoMenuVariant(role, variant, reportLevel) {
        const menuItem = {
            variant_name: variant,
            level: reportLevel,
            object_id: role.role,
            system: role.systemId,
            menu_tree: [],
        };

        menuItem["menu_tree"] = role.menu?.[0].children?.map((child) => this.parseRoleMenuChildVariant(child));

        return menuItem;
    }

    parseGroupRoleForVariant(role, variant, reportLevel, oldVariant) {
        const parsedRole: {
            variant_name: string;
            level: string;
            object_id: string;
            system: string;
            children: string[];
            old_variant_name?: string;
        } = {
            variant_name: variant,
            level: reportLevel,
            object_id: role.role,
            system: role.systemId,
            children: role.children.map((role) => role.role),
        };
        if (oldVariant) {
            parsedRole.old_variant_name = oldVariant;
        }
        return parsedRole;
    }

    parseRoleToTreeVariantWithStatus(role, variant, reportLevel, oldVariant) {
        const parsedRole: {
            variant_name: string;
            level: string;
            object_id: string;
            system: string;
            operation: string;
            delta: [];
            org_levels: [];
            old_variant_name?: string;
        } = {
            variant_name: variant,
            level: reportLevel,
            object_id: role.role,
            system: role.systemId,
            operation: role.status,
            delta: [],
            org_levels: [],
        };

        if (oldVariant) {
            parsedRole.old_variant_name = oldVariant;
        }

        return parsedRole;
    }

    parseRoleToTreeVariant(role) {
        const parsedRole = {
            object_id: role.role,
            system: role.systemId,
            initialized: role.initialized,
            delta: role.delta?.map((deltaItem) => ({
                permission_object: deltaItem.permission_object,
                permission_group: deltaItem.permission,
                field: deltaItem.field,
                operation: deltaItem.operation,
                status: deltaItem.status,
                active: deltaItem.active ?? false,
                values: deltaItem.values?.map(({ from, to }) => ({
                    value_from: from ?? "",
                    value_to: to ?? "",
                })),
            })),
            org_levels: role.orgLevelsDelta?.map((deltaItem) => ({
                org_level: deltaItem.id,
                operation: deltaItem.operation,
                values: deltaItem.values?.map(({ from, to }) => ({
                    value_from: from ?? "",
                    value_to: to ?? "",
                })),
            })),
            menu_tree: role.menu?.[0]?.children?.map((child) => this.parseRoleMenuChildVariant(child)) || [],
            children: role.children?.map((child) => ({
                description: child.description ?? "",
                role: child.role,
                system_id: child.systemId,
            })),
        };

        return parsedRole;
    }

    parseRoleChildrenVariant(children: RoleChildren[]) {
        return children.map((child) => ({
            description: child.description ?? "",
            role: child.role,
            system_id: child.systemId,
        }));
    }

    parseRoleOrgLevels(orgLevels) {
        if (orgLevels) {
            const parsedOrgLevels = orgLevels.reduce((acc, orgLevel) => {
                acc[orgLevel.id] = this.parseRoleOrgLevel(orgLevel);
                return acc;
            }, {});

            return parsedOrgLevels;
        }
    }

    parseRoleOrgLevel(orgLevel) {
        const values = orgLevel.values.map((value) => ({
            ...value,
            rowId: nanoid(),
        }));

        return {
            ...orgLevel,
            values,
        };
    }

    parseRolePermissionsTree(tree) {
        if (tree) {
            const sortedTree = sortTreeChildren(tree);

            return sortedTree.map((item) => createTreeNode(item, 0, null, this.parseRolePermissionsTreeNode, this.parseRolePermissionsTreeItem));
        }
    }

    parseRolePermissionsTreeItem(item: GetModelingRoleTreeItem | GetModelingRoleTreeField): TreeNodeResponse | FieldResponse {
        const baseFields = {
            type: item.type,
            id: item.id,
            description: item.description,
            active: item.active,
        };

        if (item.type === "field") {
            const field = item as GetModelingRoleTreeField;
            return {
                ...baseFields,
                values: field.values?.map((v) => ({ ...v, rowId: nanoid() })),
                orgLevel: field.orglevel,
                status: field.status,
                valueType: field.valueType,
            };
        }

        const treeItem = item as GetModelingRoleTreeItem;
        let children = treeItem.children;

        if (item.type === "class") {
            children = sortTreeChildren(treeItem.children);
        } else if (item.type === "permission") {
            children = treeItem.children?.map((child) => ({
                ...child,
                active: item.active,
            }));
        }

        return {
            ...baseFields,
            children,
        };
    }

    parseRolePermissionsTreeNode(node, orgLevels) {
        const parsedNode = {
            ...node,
            trafficLightStatus: calcRoleTreeItemTrafficLightStatus(node, orgLevels),
        };

        if (node.type !== "field") {
            parsedNode.status = calcRoleTreeItemStatus(node);
        }

        return parsedNode;
    }

    parseModelingRole(role) {
        const parsedRole = {
            role: role.role,
            systemId: role.system_id,
            profile: role.profile ?? "",
            description: role.description,
            isVariantAvailable: role.is_variant_available ?? false,
            isComplex: role.is_complex,
            changed: role.is_changed,
            children: (role.childrens ?? []).map((child) => ({
                role: child.role,
                description: child.description,
                systemId: child.system_id,
                isComplex: false,
                isChild: true,
                changed: child.is_changed,
            })),
            status: role.operation,
        };

        //if (parsedRole.isVariantAvailable) {
        //parsedRole.status = REPORT_MODELING_DELTA_STATUSES.CHANGED;
        // parsedRole.status = role.operation;
        // }

        return parsedRole;
    }

    parseModelingUser(user: GetModelingEmployeeResponse, roles: GetEmployeeRolesForView[]): ModelingUser {
        const parsedUser = {
            id: nanoid(),
            employee: user.employee,
            systemId: user.system_id,
            firstName: user.firstname,
            lastName: user.lastname,
            roles: (user.roles ?? [])
                .map((role) => {
                    const currentRole = roles.find((r) => role.role === r.role);
                    if (!currentRole) return null;
                    return {
                        id: role.role,
                        description: currentRole?.description,
                        isComplex: currentRole?.is_complex,
                        status: role.operation,
                        role: role.role,
                        systemId: currentRole?.system_id,
                        isStandard: currentRole?.is_standart,
                        open: false,
                        changed: currentRole?.operation == "changed",
                        children:
                            currentRole?.children?.map((child) => ({
                                role: child.role,
                                description: child.description,
                                systemId: child.system_id,
                                isComplex: false,
                                isChild: true,
                                isStandard: currentRole?.is_standart,
                                changed: child.operation == "changed",
                                id: nanoid(),
                                parentRole: role.role ?? "",
                            })) ?? [],
                    };
                })
                .filter((role) => role !== null),
        };
        return parsedUser;
    }

    parsePermissionsTreeItem(node: GetPermissionTreeItemResponse): TreeNodeResponse | FieldResponse {
        const baseNode = {
            type: node.type,
            id: node.id,
            description: node.description,
            checked: false,
            indeterminate: false,
        };

        if (node.type === "field") {
            return {
                ...baseNode,
                orgLevel: node.orglevel,
                valueType: node.valueType,
            } as FieldResponse;
        } else {
            return {
                ...baseNode,
                children: node.children,
            } as TreeNodeResponse;
        }
    }

    parsePermissionsTreeNode(node, checkedPermissions) {
        switch (node.type) {
            case "permission_object":
                return {
                    ...node,
                    checked: Boolean(checkedPermissions && checkedPermissions.has(node.id)),
                    indeterminate: false,
                };

            case "class": {
                const parsedNode = { ...node };
                setClassCheckedIndeterminate(parsedNode);
                return parsedNode;
            }

            default:
                return node;
        }
    }

    parsePermissionsTree(tree, checkedPermissions) {
        return tree.map((item) => createTreeNode(item, 0, null, (node) => this.parsePermissionsTreeNode(node, checkedPermissions), this.parsePermissionsTreeItem));
    }

    parseRoleMenuItem(roleMenuItem) {
        const parsedItem = roleMenuItem.isRoot
            ? roleMenuItem
            : {
                  reportName: roleMenuItem.report_name,
                  reportType: roleMenuItem.report_type,
                  isFolder: roleMenuItem.is_folder,
                  nodeText: roleMenuItem.node_text,
                  description: roleMenuItem.description,
              };

        if (parsedItem.isFolder) {
            parsedItem.children = roleMenuItem.children ?? [];
        }

        return parsedItem;
    }

    parseRoleMenu(roleMenu: GetModelingRoleMenuItem[]): RoleMenuNode[] {
        const root = {
            nodeText: intl.formatMessage({ id: "role-menu.root-folder-name" }),
            isRoot: true,
            isFolder: true,
            children: roleMenu,
        };

        const rootNode = createTreeNode(root, 0, null, null, (item) => this.parseRoleMenuItem(item));

        return [rootNode];
    }

    parseCopyRoleMenuNode(roleMenuNode) {
        return {
            ...roleMenuNode,
            checked: false,
            indeterminate: false,
        };
    }

    parseCopyRoleMenu(roleId, roleMenu) {
        const root = {
            nodeText: intl.formatMessage({ id: "role-menu.root-folder-name" }),
            isRoot: true,
            isFolder: true,
            children: roleMenu,
            reportName: roleId,
        };

        const rootNode = createTreeNode(
            root,
            0,
            null,
            (node) => this.parseCopyRoleMenuNode(node),
            (item) => this.parseRoleMenuItem(item)
        );

        return [rootNode];
    }
}

const reportsModelingService = new ReportsModelingService();

export { reportsModelingService };
export default ReportsModelingService;
